/*
 * TM24 Finder Fast Facets 2.0.0
 *
 * Loads the complete three-axis Finder filter catalog from a compact Shopify
 * section before the detailed search index is needed. Search ranking, SKUs,
 * variants, synonyms and server-rendered cards remain owned by the existing
 * unified controller and search core.
 */
(() => {
  'use strict';

  const VERSION = '2.2.0';
  const GLOBAL_KEY = '__tm24FastFinderFacetsV2';
  const CONTROLLER_KEY = '__tm24UnifiedSearchControllerV2';
  const FILTER_SECTION = 'tm24-unified-search-filter-index-shard';
  const FILTER_SCHEMA = 'tm24-filter-index-v1';
  const FILTER_PAGE_SIZE = 250;
  const FILTER_CONCURRENCY = 9;
  const FILTER_REQUEST_TIMEOUT = 6500;
  const FILTER_DEADLINE = 12000;
  const FILTER_RETRY_LIMIT = 1;
  const MAX_CACHE_ENTRIES = 8;
  const INSTALL_RETRY_LIMIT = 200;
  const INSTALL_RETRY_DELAY = 25;
  const FAILSAFE_DELAY = 6000;
  const cache = new Map();
  const CACHE_TTL = 120000;
  const SESSION_KEY = 'tm24:compact-filters:2.2.0';
  const inflight = new Map();
  const preparedRoots = new Set();
  let installed = false;
  let installAttempts = 0;
  let installTimer = 0;
  let failsafeTimer = 0;
  let originalLoadOwnedFacets = null;

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].prepare(document);
    window[GLOBAL_KEY].install();
    return;
  }

  function now() {
    return Date.now();
  }

  function safeInteger(value, fallback = 0) {
    const number = Number.parseInt(String(value ?? ''), 10);
    return Number.isSafeInteger(number) && number >= 0 ? number : fallback;
  }

  function sourceError(message, code = 'fast_filter_source_error') {
    const error = new Error(message);
    error.code = code;
    return error;
  }

  function isAbort(error) {
    return error?.name === 'AbortError';
  }

  function cacheRead(key) {
    let entry = cache.get(key);
    if (!entry) {
      try {
        const saved = JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null');
        if (saved?.sourceKey === key && saved.dataset?.complete && Array.isArray(saved.dataset.products)
          && saved.dataset.products.length === saved.dataset.expected
          && saved.dataset.products.every(product => product?.id != null && product._tm24SourceKey)) entry = saved.dataset;
      } catch (_) {}
    }
    const age = now() - Number(entry?.fetchedAt);
    if (!entry || !Number.isFinite(age) || age < 0 || age > CACHE_TTL) {
      cache.delete(key);
      return null;
    }
    cache.delete(key);
    cache.set(key, entry);
    return entry;
  }

  function cacheWrite(key, value) {
    cache.delete(key);
    cache.set(key, value);
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify({ sourceKey: key, dataset: value })); } catch (_) {}
    while (cache.size > MAX_CACHE_ENTRIES) {
      cache.delete(cache.keys().next().value);
    }
    return value;
  }

  function prepare(scope = document) {
    scope.querySelectorAll?.('[data-tm24-search-root][data-tm24-search-enabled="true"]')
      .forEach((root) => {
        if (root.dataset.context !== 'finder' || preparedRoots.has(root)) return;
        preparedRoots.add(root);
        root.dataset.tm24FastFacetsPending = 'true';
        root.setAttribute('data-tm24-search-enabled', 'pending');
      });
  }

  function restorePreparedRoots() {
    preparedRoots.forEach((root) => {
      if (!root.isConnected) return;
      root.setAttribute('data-tm24-search-enabled', 'true');
      delete root.dataset.tm24FastFacetsPending;
    });
    preparedRoots.clear();
  }

  function normalizedParams(source) {
    const params = new URLSearchParams(source?.params || '');
    params.delete('section_id');
    params.delete('page');
    params.delete('sort_by');
    params.delete('tm24_filter_request');
    [...params.keys()].forEach((key) => {
      if (key.startsWith('filter.')) params.delete(key);
    });
    params.sort();
    return params;
  }

  function filterSource(source) {
    const path = String(source?.path || '');
    const params = normalizedParams(source);
    const expected = safeInteger(source?.expected);
    const locale = (document.documentElement.lang || 'de').toLowerCase();
    const market = window.Shopify?.country || document.documentElement.dataset.country || '';
    const currency = window.Shopify?.currency?.active || '';
    const key = [
      location.host,
      locale,
      market,
      currency,
      FILTER_SCHEMA,
      VERSION,
      source?.key || '',
      expected,
      path,
      params.toString(),
    ].join('|');
    return { key, path, params, expected, sourceKey: source?.key || key };
  }

  function sectionUrl(source, page, requestToken) {
    const url = new URL(source.path, location.origin);
    const params = new URLSearchParams(source.params);
    params.set('section_id', FILTER_SECTION);
    params.set('page', String(page));
    params.set('tm24_filter_request', requestToken);
    url.search = params.toString();
    return url;
  }

  async function fetchHtmlAttempt(url, signal) {
    const controller = new AbortController();
    let timedOut = false;
    const abortFromParent = () => controller.abort();
    if (signal?.aborted) controller.abort();
    else signal?.addEventListener('abort', abortFromParent, { once: true });
    const timeout = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, FILTER_REQUEST_TIMEOUT);
    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        signal: controller.signal,
      });
      if (!response.ok) throw sourceError(`HTTP ${response.status}`, 'fast_filter_http_error');
      return await response.text();
    } catch (error) {
      if (timedOut) throw sourceError('Fast filter request timed out', 'fast_filter_timeout');
      throw error;
    } finally {
      clearTimeout(timeout);
      signal?.removeEventListener('abort', abortFromParent);
    }
  }

  async function fetchHtml(url, signal) {
    let attempt = 0;
    while (true) {
      try {
        return await fetchHtmlAttempt(url, signal);
      } catch (error) {
        if (isAbort(error) || signal?.aborted || attempt >= FILTER_RETRY_LIMIT) throw error;
        attempt += 1;
      }
    }
  }

  function parseShard(html, requestedPage, source) {
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    const wrapper = parsedDocument.querySelector('[data-tm24-filter-index-shard]');
    const payload = wrapper?.querySelector('[data-tm24-filter-products]');
    if (!wrapper || !payload) {
      throw sourceError('Missing fast filter shard markup', 'fast_filter_invalid_markup');
    }
    if (wrapper.dataset.schemaVersion !== FILTER_SCHEMA) {
      throw sourceError('Unexpected fast filter schema', 'fast_filter_schema_mismatch');
    }

    const page = safeInteger(wrapper.dataset.page);
    const pages = safeInteger(wrapper.dataset.pages);
    const pageSize = safeInteger(wrapper.dataset.pageSize);
    const pageItems = safeInteger(wrapper.dataset.pageItems);
    const expected = safeInteger(wrapper.dataset.expectedCount);
    if (
      !page
      || (expected > 0 && !pages)
      || !pageSize
      || page !== requestedPage
      || page > Math.max(1, pages)
      || pageSize !== FILTER_PAGE_SIZE
      || pageItems > pageSize
    ) {
      throw sourceError('Invalid fast filter pagination metadata', 'fast_filter_pagination_mismatch');
    }
    if (source.expected > 0 && expected !== source.expected) {
      throw sourceError('Finder scope changed while filter data loaded', 'fast_filter_scope_changed');
    }

    let products;
    try {
      products = JSON.parse(payload.textContent || '[]');
    } catch (_error) {
      throw sourceError('Invalid fast filter JSON', 'fast_filter_invalid_json');
    }
    if (!Array.isArray(products) || products.length !== pageItems) {
      throw sourceError('Fast filter item count mismatch', 'fast_filter_item_count_mismatch');
    }
    products.forEach((product) => {
      if (!product || product.id == null) {
        throw sourceError('Incomplete fast filter product record', 'fast_filter_invalid_product');
      }
    });
    return { page, pages: Math.max(1, pages), pageSize, pageItems, expected, products };
  }

  async function pooled(values, worker, signal) {
    let cursor = 0;
    const count = Math.min(FILTER_CONCURRENCY, values.length);
    const workers = Array.from({ length: count }, async () => {
      while (cursor < values.length) {
        if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
        const current = values[cursor];
        cursor += 1;
        await worker(current);
      }
    });
    await Promise.all(workers);
  }

  async function withDeadline(signal, operation) {
    const controller = new AbortController();
    let timedOut = false;
    const abortFromParent = () => controller.abort();
    if (signal?.aborted) controller.abort();
    else signal?.addEventListener('abort', abortFromParent, { once: true });
    const timeout = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, FILTER_DEADLINE);
    try {
      return await operation(controller.signal);
    } catch (error) {
      if (timedOut) throw sourceError('Fast filter deadline exceeded', 'fast_filter_deadline');
      throw error;
    } finally {
      clearTimeout(timeout);
      signal?.removeEventListener('abort', abortFromParent);
      controller.abort();
    }
  }

  async function fetchCompleteFilterIndex(source, signal) {
    return withDeadline(signal, async (deadlineSignal) => {
      const requestToken = `filter-${now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      const shards = new Map();
      let first;
      const count = Math.ceil(source.expected / FILTER_PAGE_SIZE);
      if (!count) {
        first = parseShard(await fetchHtml(sectionUrl(source, 1, requestToken), deadlineSignal), 1, source);
        shards.set(1, first);
      }
      const remaining = Array.from({ length: count || first.pages }, (_, index) => index + 1)
        .filter(page => !shards.has(page));
      await pooled(remaining, async (page) => {
        const html = await fetchHtml(sectionUrl(source, page, requestToken), deadlineSignal);
        const shard = parseShard(html, page, source);
        shards.set(page, shard);
      }, deadlineSignal);
      first = shards.get(1);
      shards.forEach(shard => {
        if (
          shard.pages !== first.pages
          || shard.pageSize !== first.pageSize
          || shard.expected !== first.expected
        ) {
          throw sourceError('Finder scope changed between filter pages', 'fast_filter_catalog_changed');
        }
      });

      if (shards.size !== first.pages) {
        throw sourceError('Not every fast filter page loaded', 'fast_filter_incomplete_pages');
      }
      const products = [];
      const identifiers = new Set();
      [...shards.keys()].sort((left, right) => left - right).forEach((page) => {
        shards.get(page).products.forEach((product) => {
          const identifier = String(product.id);
          if (identifiers.has(identifier)) {
            throw sourceError('Duplicate product in fast filter index', 'fast_filter_duplicate_product');
          }
          identifiers.add(identifier);
          products.push(Object.freeze({
            id: product.id,
            manufacturer: String(product.manufacturer || ''),
            gateType: String(product.gateType || ''),
            productType: String(product.productType || ''),
            _tm24SourceKey: source.sourceKey,
            _tm24SourcePath: source.path,
            _tm24SourceParams: source.params.toString(),
            _tm24Ordinal: products.length,
            _tm24CardPage: Math.floor(products.length / 50) + 1,
          }));
        });
      });
      if (products.length !== first.expected) {
        throw sourceError(
          `Expected ${first.expected} filter records, received ${products.length}`,
          'fast_filter_incomplete_index',
        );
      }
      return Object.freeze({
        key: source.sourceKey,
        path: source.path,
        params: source.params.toString(),
        products: Object.freeze(products),
        expected: first.expected,
        pages: first.pages,
        pageSize: first.pageSize,
        fetchedAt: now(),
        validatedAt: now(),
        complete: true,
        fastFacets: true,
      });
    });
  }

  function attachOwner(entry, signal) {
    if (!signal) return true;
    if (signal.aborted) return false;
    if (entry.owners.has(signal)) return true;
    const release = () => {
      entry.owners.delete(signal);
      entry.ownerListeners.delete(signal);
      if (!entry.settled && entry.owners.size === 0) entry.controller.abort();
    };
    entry.owners.add(signal);
    entry.ownerListeners.set(signal, release);
    signal.addEventListener('abort', release, { once: true });
    return true;
  }

  function loadFilterIndex(sourceInput, signal, force = false) {
    if (signal?.aborted) return Promise.reject(new DOMException('Aborted', 'AbortError'));
    const source = filterSource(sourceInput);
    if (!source.path) return Promise.reject(sourceError('Missing Finder scope path', 'fast_filter_missing_scope'));
    if (!force) {
      const cached = cacheRead(source.key);
      if (cached?.complete) return Promise.resolve(cached);
    }

    const current = inflight.get(source.key);
    if (current && !current.controller.signal.aborted && attachOwner(current, signal)) {
      return current.promise;
    }
    if (current) inflight.delete(source.key);

    const entry = {
      controller: new AbortController(),
      owners: new Set(),
      ownerListeners: new Map(),
      settled: false,
      promise: null,
    };
    if (!attachOwner(entry, signal)) {
      return Promise.reject(new DOMException('Aborted', 'AbortError'));
    }
    const promise = fetchCompleteFilterIndex(source, entry.controller.signal)
      .catch((error) => {
        if (entry.controller.signal.aborted || !source.expected
          || !['fast_filter_scope_changed', 'fast_filter_pagination_mismatch', 'fast_filter_catalog_changed'].includes(error?.code)) throw error;
        return fetchCompleteFilterIndex({ ...source, expected: 0 }, entry.controller.signal);
      })
      .then((dataset) => cacheWrite(source.key, dataset))
      .finally(() => {
        entry.settled = true;
        entry.ownerListeners.forEach((listener, ownerSignal) => {
          ownerSignal.removeEventListener('abort', listener);
        });
        entry.ownerListeners.clear();
        entry.owners.clear();
        if (inflight.get(source.key) === entry) inflight.delete(source.key);
      });
    entry.promise = promise;
    inflight.set(source.key, entry);
    return promise;
  }

  function patchController() {
    const api = window.TM24FinderFilters;
    const controller = window[CONTROLLER_KEY];
    const UnifiedSearch = api?.controller?.UnifiedSearch;
    if (!controller?.mount || !UnifiedSearch?.prototype) return false;
    const prototype = UnifiedSearch.prototype;
    if (prototype.__tm24FastFacetsV2 === VERSION) return true;
    if (typeof prototype.loadOwnedFacets !== 'function') return false;

    originalLoadOwnedFacets = prototype.loadOwnedFacets;
    prototype.loadFinderFilterDataset = function loadFinderFilterDataset(force = false) {
      return loadFilterIndex(this.source(false), this.finderIndexController.signal, force);
    };
    prototype.loadOwnedFacets = function loadOwnedFacetsFast(force = false) {
      if (this.context !== 'finder' || !this.facetsHost || !this.facetsContent) {
        return originalLoadOwnedFacets.call(this, force);
      }
      if (this.finderIndexController.signal.aborted) {
        this.finderIndexController = new AbortController();
      }
      if (this.finderFilterLoad) return this.finderFilterLoad;
      if (!force && this.finderFilterCatalog && now() - this.finderCatalogFetchedAt <= CACHE_TTL) {
        this.renderFinderFacets(this.finderFilterProducts);
        return Promise.resolve();
      }

      const ownSerial = ++this.finderFacetSerial;
      const startedAt = now();
      this.facetsHost.setAttribute('aria-busy', 'true');
      if (!this.finderFilterCatalog) this.renderFinderFacetLoading();
      if (this.facetsStatus) {
        this.facetsStatus.textContent = this.root.dataset.filterLoading || this.root.dataset.loadingText;
        this.facetsStatus.hidden = false;
      }
      if (this.facetsRetry) {
        this.facetsRetry.textContent = this.root.dataset.filterRetry || this.facetsRetry.textContent;
        this.facetsRetry.disabled = true;
        this.facetsRetry.hidden = true;
      }
      this.root.dataset.tm24FastFacets = 'loading';

      const request = (async () => {
        try {
          const dataset = await loadFilterIndex(
            this.source(false),
            this.finderIndexController.signal,
            force,
          );
          if (
            this.finderIndexController.signal.aborted
            || this.destroyed
            || ownSerial !== this.finderFacetSerial
          ) return;
          const query = this.input.value.trim();
          const currentQueryReady = query && this.finderFilterProductsReady
            && this.finderFilterProductsQuery === query
            && this.finderFilterProductsSourceVersion === this.finderCatalogSourceVersion;
          if (!currentQueryReady) {
            this.adoptFinderCatalogDataset(dataset);
            if (!query) this.setFinderFilterProducts(dataset.products, '', dataset);
            this.renderFinderFacets(query ? [] : dataset.products);
            if (query) this.markFinderFacetCountsPending();
          }
          if (this.facetsStatus) this.facetsStatus.hidden = true;
          if (this.facetsRetry) {
            this.facetsRetry.disabled = false;
            this.facetsRetry.hidden = true;
          }
          this.root.dataset.tm24FastFacets = 'ready';
          this.root.dataset.tm24FastFacetsProducts = String(dataset.expected);
          this.root.dataset.tm24FastFacetsPages = String(dataset.pages);
          this.root.dataset.tm24FastFacetsDuration = String(Math.max(0, now() - startedAt));
        } catch (error) {
          if (isAbort(error) || this.destroyed || ownSerial !== this.finderFacetSerial) return;
          this.root.dataset.tm24FastFacets = `error:${String(error?.code || 'source_error')}`;
          if (!this.finderFilterCatalog) this.renderFinderFacetError();
          else this.markFinderFacetCountsPending();
          if (this.facetsStatus) {
            this.facetsStatus.textContent = this.root.dataset.filterError || this.root.dataset.errorText;
            this.facetsStatus.hidden = false;
          }
          if (this.facetsRetry) {
            this.facetsRetry.disabled = false;
            this.facetsRetry.hidden = false;
          }
        } finally {
          if (ownSerial === this.finderFacetSerial) {
            this.facetsHost.setAttribute('aria-busy', 'false');
            this.facetsContent?.querySelectorAll('[aria-busy="true"]').forEach((node) => {
              node.setAttribute('aria-busy', 'false');
            });
          }
          if (this.finderFilterLoad === request) this.finderFilterLoad = null;
        }
      })();
      this.finderFilterLoad = request;
      return request;
    };
    Object.defineProperty(prototype, '__tm24FastFacetsV2', {
      configurable: false,
      enumerable: false,
      writable: false,
      value: VERSION,
    });
    return true;
  }

  function mountPreparedRoots() {
    restorePreparedRoots();
    window[CONTROLLER_KEY]?.mount?.(document);
  }

  function failSafe() {
    if (installed) return;
    mountPreparedRoots();
  }

  function install() {
    if (installed) {
      mountPreparedRoots();
      return true;
    }
    try {
      if (!patchController()) {
        installAttempts += 1;
        if (installAttempts <= INSTALL_RETRY_LIMIT) {
          clearTimeout(installTimer);
          installTimer = window.setTimeout(install, INSTALL_RETRY_DELAY);
          return false;
        }
        failSafe();
        return false;
      }
      installed = true;
      clearTimeout(installTimer);
      clearTimeout(failsafeTimer);
      mountPreparedRoots();
      return true;
    } catch (_error) {
      failSafe();
      return false;
    }
  }

  const publicApi = Object.freeze({
    VERSION,
    prepare,
    install,
    restore: restorePreparedRoots,
    get installed() { return installed; },
    get cacheEntries() { return cache.size; },
    get inflightRequests() { return inflight.size; },
    constants: Object.freeze({
      FILTER_SECTION,
      FILTER_SCHEMA,
      FILTER_PAGE_SIZE,
      FILTER_CONCURRENCY,
      FILTER_REQUEST_TIMEOUT,
      FILTER_DEADLINE,
      FILTER_RETRY_LIMIT,
    }),
  });
  window[GLOBAL_KEY] = publicApi;

  prepare(document);
  failsafeTimer = window.setTimeout(failSafe, FAILSAFE_DELAY);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', install, { once: true });
  } else {
    window.setTimeout(install, 0);
  }
})();
